// Static scenery drawn in a few draws: every plain mesh under a root that
// shares a material (and the same shadow flags) is merged into one mesh, in
// the root's own space. The pieces stay where they were, for everything that
// reads the scene on the CPU (picking boxes, the walk grid, the camera's
// framing), but move to layer 1, which no camera draws and no raycaster hits;
// the merged mesh is drawn and hit instead, and it lives under the same root,
// so hiding the root hides it.
//
// What's left alone, drawn as it was:
//   - anything under an object with its own userData (a door leaf that
//     swings, a drape, a lamp that lights, snow that comes and goes, a thing
//     that's picked), apart from a few harmless marks (HARMLESS);
//   - instanced, skinned and morphing meshes, mirrored ones (their faces
//     would turn inside out), hidden ones, and ones with their own
//     onBeforeRender;
//   - a material used once (nothing to gain).
// Call it again after a rebuild; mergeStatic is idempotent on a root.
import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { shapeKey, vertexColoured } from './materials.js';

export const DRAWN = 0, HIDDEN = 1;
// marks that say nothing about behaviour: they don't stop a merge
const HARMLESS = new Set(['isSphere', 'noShadow', 'noReceive', 'noAO']);
const plain = (o: THREE.Object3D) => { for (const k in o.userData) if (!HARMLESS.has(k)) return false; return true; };
const baseBeforeRender = THREE.Object3D.prototype.onBeforeRender;

type Piece = { geo: THREE.BufferGeometry; start: number; count: number; matrix: THREE.Matrix4; paint?: THREE.Color };
const sig = (g: THREE.BufferGeometry) => Object.keys(g.attributes).sort().map((k) => `${k}${g.attributes[k].itemSize}${(g.attributes[k] as any).normalized ? 'n' : ''}`).join(',');

const UV = /^uv\d*$/;
const sigPainted = (g: THREE.BufferGeometry) => Object.keys(g.attributes).filter((k) => !UV.test(k)).concat('color').sort().map((k) => k === 'color' ? 'color3' : `${k}${g.attributes[k].itemSize}${(g.attributes[k] as any).normalized ? 'n' : ''}`).join(',');

export type MergeStats = { meshes: number; merged: number; draws: number };

export function mergeStatic(root: THREE.Object3D, opts: { min?: number; recolour?: boolean } = {}): MergeStats {
  const min = opts.min ?? 2;
  // a second call first undoes the first (the root may have been rebuilt in part)
  unmerge(root);
  root.updateMatrixWorld(true);
  const inv = new THREE.Matrix4().copy(root.matrixWorld).invert();
  const groups = new Map<string, { mat: THREE.Material; cast: boolean; receive: boolean; order: number; pieces: Piece[]; meshes: Set<THREE.Mesh> }>();
  let meshes = 0;
  const visit = (o: THREE.Object3D) => {
    if (!o.visible) return;
    if (o !== root && !plain(o)) return;
    const m = o as THREE.Mesh;
    if (m.isMesh) {
      meshes++;
      const g = m.geometry as THREE.BufferGeometry;
      const ok = !(m as any).isInstancedMesh && !(m as any).isSkinnedMesh && g?.attributes?.position && !Object.keys(g.morphAttributes || {}).length
        && !Object.values(g.attributes).some((a: any) => a.isInterleavedBufferAttribute) && m.onBeforeRender === baseBeforeRender && (m.layers.mask & ~(1 << 7)) === 1 && m.matrixWorld.determinant() > 0;
      if (ok) {
        const matrix = new THREE.Matrix4().multiplyMatrices(inv, m.matrixWorld);
        const n = g.index ? g.index.count : g.attributes.position.count;
        const r0 = g.drawRange.start, r1 = Math.min(n, r0 + g.drawRange.count);
        const parts: { mat: THREE.Material; start: number; count: number }[] = [];
        if (Array.isArray(m.material)) {
          for (const gr of g.groups) { const mat = m.material[gr.materialIndex ?? 0]; if (mat) parts.push({ mat, start: Math.max(gr.start, r0), count: Math.min(gr.start + gr.count, r1) - Math.max(gr.start, r0) }); }
        } else parts.push({ mat: m.material, start: r0, count: r1 - r0 });
        for (const p of parts) {
          if (p.count <= 0 || !p.mat.visible) continue;
          // a plain colour goes into the vertices, so pieces of every colour share one material
          const shape = opts.recolour === false || g.attributes.color ? null : shapeKey(p.mat);
          const key = `${shape ? `vc:${shape}` : p.mat.uuid}|${m.castShadow ? 1 : 0}${m.receiveShadow ? 1 : 0}|${m.renderOrder}|${shape ? sigPainted(g) : sig(g)}`;
          let e = groups.get(key);
          if (!e) { e = { mat: shape ? vertexColoured(p.mat, shape) : p.mat, cast: m.castShadow, receive: m.receiveShadow, order: m.renderOrder, pieces: [], meshes: new Set() }; groups.set(key, e); }
          e.pieces.push({ geo: g, start: p.start, count: p.count, matrix, paint: shape ? (p.mat as any).color : undefined });
          e.meshes.add(m);
        }
      }
    }
    for (const c of o.children) visit(c);
  };
  visit(root);

  // a mesh is hidden only when every part of it goes into a merge; a group with
  // a mesh that can't be hidden is left out (its parts would be drawn twice)
  const all = [...groups.values()];
  const plan = new Set(all.filter((e) => e.pieces.length >= min));
  for (let changed = true; changed;) {
    changed = false;
    const bad = new Set<THREE.Mesh>();
    for (const e of all) if (!plan.has(e)) for (const m of e.meshes) bad.add(m);
    for (const e of plan) if ([...e.meshes].some((m) => bad.has(m))) { plan.delete(e); changed = true; }
  }
  const out: THREE.Mesh[] = [];
  const hide = new Set<THREE.Mesh>();
  for (const e of plan) {
    const geos = e.pieces.map(slice);
    const merged = mergeGeometries(geos, false);
    for (const gg of geos) gg.dispose();
    if (!merged) continue;
    merged.computeBoundingSphere(); merged.computeBoundingBox();
    const mesh = new THREE.Mesh(merged, e.mat);
    mesh.castShadow = e.cast; mesh.receiveShadow = e.receive; mesh.renderOrder = e.order;
    mesh.userData = { merged: true, walkSkip: true, noCollide: true, ...(e.cast ? {} : { noShadow: true }) };
    mesh.matrixAutoUpdate = false;
    out.push(mesh);
    for (const m of e.meshes) hide.add(m);
  }
  let hidden = 0;
  for (const m of hide) { m.layers.disable(DRAWN); m.layers.disable(3); m.layers.enable(HIDDEN); hidden++; }
  if (out.length) { root.add(...out); root.userData.mergedMeshes = out; }
  return { meshes, merged: hidden, draws: out.length };
}

// The merged meshes go, and the pieces are drawn again.
export function unmerge(root: THREE.Object3D) {
  const out = root.userData.mergedMeshes as THREE.Mesh[] | undefined;
  if (!out) return;
  for (const m of out) { m.removeFromParent(); m.geometry.dispose(); }
  delete root.userData.mergedMeshes;
  root.traverse((o) => { if (o.layers.isEnabled(HIDDEN)) { o.layers.disable(HIDDEN); o.layers.enable(DRAWN); } });
}

// One piece of a geometry (its draw range or material group), moved into place
// and indexed, so pieces of every kind merge together.
function slice(p: Piece) {
  const g = p.geo;
  const out = new THREE.BufferGeometry();
  const idx = g.index;
  const used: number[] = [];
  const remap = new Map<number, number>();
  const index: number[] = [];
  for (let i = p.start; i < p.start + p.count; i++) {
    const v = idx ? idx.getX(i) : i;
    let k = remap.get(v);
    if (k === undefined) { k = used.length; remap.set(v, k); used.push(v); }
    index.push(k);
  }
  for (const name of Object.keys(g.attributes)) {
    if (p.paint && UV.test(name)) continue;
    const a = g.attributes[name] as THREE.BufferAttribute;
    const arr = new (a.array.constructor as any)(used.length * a.itemSize);
    for (let j = 0; j < used.length; j++) for (let c = 0; c < a.itemSize; c++) arr[j * a.itemSize + c] = a.getComponent(used[j], c);
    out.setAttribute(name, new THREE.BufferAttribute(arr, a.itemSize, a.normalized));
  }
  if (p.paint) {
    const c = new Float32Array(used.length * 3);
    for (let j = 0; j < used.length; j++) { c[j * 3] = p.paint.r; c[j * 3 + 1] = p.paint.g; c[j * 3 + 2] = p.paint.b; }
    out.setAttribute('color', new THREE.BufferAttribute(c, 3));
  }
  out.setIndex(index);
  out.applyMatrix4(p.matrix);
  return out;
}

// Each root merged on its own (a thing stays one pickable piece).
export function mergeAll(roots: THREE.Object3D[], opts: { min?: number } = {}) {
  const t: MergeStats = { meshes: 0, merged: 0, draws: 0 };
  for (const r of roots) { const s = mergeStatic(r, opts); t.meshes += s.meshes; t.merged += s.merged; t.draws += s.draws; }
  const w = window as any;
  w.__merge = t;
  return t;
}
